/**
 * Issues page: GET summary/alert_counts and GET summary/items in parallel.
 * Supports abort via vm._issuesRequestGate + stale-generation guards.
 */

import {
  buildSummaryRequestParams,
  watchdogTypeToApiParam,
  parseAlertCountsBody,
  parseAlertCountsHttpPayload,
  parseSummaryJsonPayload,
  summaryItemsToLegacyMaps
} from './watchdogSummaryApi.js'
import { createRequestGate, isRequestAborted } from './requestGate.js'

function issuesBaseParams (vm) {
  return buildSummaryRequestParams(vm.filterByMarket, vm.filterBySellAcc, vm.dateRange, {
    start: vm.customDateStart,
    end: vm.customDateEnd
  })
}

function ensureIssuesGate (vm) {
  if (!vm._issuesRequestGate) vm._issuesRequestGate = createRequestGate()
  return vm._issuesRequestGate
}

/**
 * vue-resource treats 304 as failed; body is often empty. Match thresholds panels: no-store + retry.
 */
function getSummaryAlertCounts (vm, alertParams, gate, gen) {
  var baseOpts = {
    params: alertParams,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    }
  }
  var opts = gate ? gate.trackOpts(baseOpts) : baseOpts
  return vm.$http
    .get('/api/v1/watchdogs/summary/alert_counts', opts)
    .catch(function (res) {
      if (gate && gen != null && !gate.isCurrent(gen)) return Promise.reject(res)
      if (res && res.status === 304) {
        var retryParams = Object.assign({}, alertParams, { _skretry: Date.now() })
        var retryOpts = gate
          ? gate.trackOpts({
            params: retryParams,
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache'
            }
          })
          : {
            params: retryParams,
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache'
            }
          }
        return vm.$http.get('/api/v1/watchdogs/summary/alert_counts', retryOpts)
      }
      return Promise.reject(res)
    })
}

export function computeWatchdogTypesForItemsFetch (vm, host) {
  return computeTypesToFetch(vm, host.wdList || [])
}

function computeTypesToFetch (vm, wdList) {
  var wdSet = {}
  for (var wi = 0; wi < wdList.length; wi++) wdSet[wdList[wi]] = true

  var types = []
  var dw = vm.displayedWatchdogs || []
  for (var di = 0; di < dw.length; di++) {
    if (wdSet[dw[di]]) types.push(dw[di])
  }

  if (types.length === 0 && wdList.length > 0) {
    if (vm.activeCategory && wdList.indexOf(vm.activeCategory) >= 0) {
      types = [vm.activeCategory]
    } else {
      types = wdList.slice()
    }
  }

  return types
}

function clearStaleWatchdogItems (vm, host, wdList, types) {
  for (var ci = 0; ci < wdList.length; ci++) {
    var wClear = wdList[ci]
    if (types.indexOf(wClear) >= 0) continue
    var wdClear = host.worldStore.watchDogs[wClear]
    if (wdClear) {
      vm.$set(wdClear, 'items', {})
      vm.$delete(wdClear, 'summaryTotals')
    }
  }
}

function filtersAllowUnscopedPrefetch (vm) {
  return (!vm.filterByMarket || vm.filterByMarket.length === 0) &&
    (!vm.filterBySellAcc || vm.filterBySellAcc.length === 0) &&
    (!vm.dateRange || vm.dateRange === 'all' || vm.dateRange === 'all_time')
}

/**
 * GET summary/items for each frontend watchdog type in `types`.
 * Aborts prior Issues summary requests when a new gate generation starts.
 */
export function fetchSummaryItemsForTypes (vm, host, types, onDone) {
  function finish () {
    if (typeof onDone === 'function') onDone()
  }

  var gate = ensureIssuesGate(vm)
  var gen = gate.generation()
  var baseParams = issuesBaseParams(vm)
  var wdList = host.wdList || []

  clearStaleWatchdogItems(vm, host, wdList, types)
  vm.issuesItemsLoading = true

  if (!types || types.length === 0) {
    vm.issuesItemsLoading = false
    finish()
    return
  }

  var pending = types.length
  function oneItemDone () {
    pending -= 1
    if (pending <= 0) {
      if (gate.isCurrent(gen)) vm.issuesItemsLoading = false
      finish()
    }
  }

  types.forEach(function (t) {
    var iparams = Object.assign({}, baseParams, {
      watchdog_type: watchdogTypeToApiParam(t),
      page: vm.issuesListPage || 1,
      per_page: 10,
      list: vm.issuesResolvedHidden
        ? 'active'
        : (vm.issuesListTab === 'resolved' ? 'resolved' : 'active'),
      _cb: Date.now()
    })
    if (vm.issuesSearchQuery) iparams.q = vm.issuesSearchQuery
    if (t === 'fbaFeeChange' && vm.issuesFeeDirection) iparams.fee_direction = vm.issuesFeeDirection
    if (!vm.issuesResolvedHidden && vm.issuesFocusPending && vm.highlightedAlertId) {
      iparams.focus_watchdog_id = vm.highlightedAlertId
    }

    vm.$http
      .get('/api/v1/watchdogs/summary/items', gate.trackOpts({
        params: iparams,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      }))
      .then(function (ir) {
        if (!gate.isCurrent(gen)) {
          oneItemDone()
          return
        }
        var body = parseSummaryJsonPayload(ir)
        var meta = body && body.meta
        if (meta && (meta.resolved_hidden === true || meta.resolvedHidden === true)) {
          vm.issuesResolvedHidden = true
          if (vm.issuesListTab === 'resolved') vm.issuesListTab = 'active'
        } else if (meta) {
          vm.issuesResolvedHidden = false
        }
        if (meta && meta.list && meta.list !== vm.issuesListTab) {
          vm.issuesListTab = meta.list
        }
        if (meta && meta.page) vm.issuesListPage = meta.page
        vm.issuesItemsMeta = meta || null
        vm.issuesFocusPending = false
        var maps = summaryItemsToLegacyMaps(body, t)
        var mergeProducts = maps.productsPatch || {}
        Object.keys(mergeProducts).forEach(function (pk) {
          vm.$set(vm.issueProductsMap, pk, mergeProducts[pk])
        })
        var wdHost = host.worldStore.watchDogs[t]
        if (wdHost) {
          vm.$set(wdHost, 'items', maps.items || {})
          if (maps.summaryTotals == null) {
            vm.$delete(wdHost, 'summaryTotals')
          } else {
            vm.$set(wdHost, 'summaryTotals', maps.summaryTotals)
          }
        }
        vm.$forceUpdate()
        if (typeof vm.tryFocusDeepLinkedAlert === 'function') {
          vm.$nextTick(function () {
            vm.tryFocusDeepLinkedAlert()
          })
        }
        oneItemDone()
      })
      .catch(function (itemErr) {
        if (!gate.isCurrent(gen) || isRequestAborted(itemErr)) {
          oneItemDone()
          return
        }
        console.error('[IssuesPage] summary/items failed for', t, itemErr)
        var wdFail = host.worldStore.watchDogs[t]
        if (wdFail) {
          vm.$set(wdFail, 'items', {})
          vm.$delete(wdFail, 'summaryTotals')
        }
        vm.$forceUpdate()
        oneItemDone()
      })
  })

  if (vm._pendingIssuesScrollCategory) {
    var c = vm._pendingIssuesScrollCategory
    vm._pendingIssuesScrollCategory = null
    vm.$nextTick(function () {
      if (typeof vm.scrollIssuesListToCategory === 'function') {
        vm.scrollIssuesListToCategory(c)
      }
    })
  }
}

export function refreshIssuesSummaryQuiet (vm, host, onDone) {
  function finish () {
    if (typeof onDone === 'function') onDone()
  }

  var gate = ensureIssuesGate(vm)
  var gen = gate.begin()
  var baseParams = issuesBaseParams(vm)
  var alertParams = Object.assign({}, baseParams, { _cb: Date.now() })
  var types = computeWatchdogTypesForItemsFetch(vm, host)

  var pending = 2
  function oneDone () {
    pending -= 1
    if (pending <= 0) finish()
  }

  fetchSummaryItemsForTypes(vm, host, types, oneDone)

  getSummaryAlertCounts(vm, alertParams, gate, gen)
    .then(function (response) {
      if (!gate.isCurrent(gen)) {
        oneDone()
        return
      }
      try {
        var countsPayload = parseAlertCountsHttpPayload(response)
        vm.summaryAlertCounts = parseAlertCountsBody(countsPayload, null)
      } catch (parseErr) {
        console.error('[IssuesPage] alert_counts quiet refresh parse failed:', parseErr)
      }
      vm.lastUpdateTime = Date.now()
      vm.lastUpdated = 'just now'
      vm.updateFilters()
      vm.$forceUpdate()
      oneDone()
    })
    .catch(function (err) {
      if (!isRequestAborted(err)) {
        console.error('[IssuesPage] alert_counts quiet refresh failed:', err)
      }
      oneDone()
    })
}

/**
 * Apply root prefetch of unfiltered alert_counts when filters are still open.
 * @returns {boolean} true if applied
 */
function applyPrefetchedAlertCounts (vm) {
  if (!filtersAllowUnscopedPrefetch(vm)) return false
  var root = vm.$root
  var pref = root && root._prefetchedAlertCounts
  if (!pref || !pref.payload || pref.filterKey !== 'all') return false
  if (Date.now() - pref.at > 60000) return false
  try {
    vm.summaryAlertCounts = parseAlertCountsBody(pref.payload, null)
    return true
  } catch (e) {
    return false
  }
}

export function runIssuesSummaryFetchCore (vm, host, onDone) {
  function finish () {
    if (typeof onDone === 'function') onDone()
  }

  var gate = ensureIssuesGate(vm)
  var gen = gate.begin()
  var baseParams = issuesBaseParams(vm)
  var alertParams = Object.assign({}, baseParams, { _cb: Date.now() })
  var wdList = host.wdList || []
  var types = computeTypesToFetch(vm, wdList)

  var pending = 2
  function oneDone () {
    pending -= 1
    if (pending <= 0) finish()
  }

  fetchSummaryItemsForTypes(vm, host, types, function () {
    if (!gate.isCurrent(gen)) {
      oneDone()
      return
    }
    vm.loading = false
    vm.lastUpdateTime = Date.now()
    vm.lastUpdated = 'just now'
    vm.updateFilters()
    vm.$forceUpdate()
    oneDone()
  })

  if (applyPrefetchedAlertCounts(vm)) {
    vm.updateFilters()
    vm.$forceUpdate()
    oneDone()
    return
  }

  getSummaryAlertCounts(vm, alertParams, gate, gen)
    .then(function (response) {
      if (!gate.isCurrent(gen)) {
        oneDone()
        return
      }
      try {
        try {
          var countsPayload = parseAlertCountsHttpPayload(response)
          vm.summaryAlertCounts = parseAlertCountsBody(countsPayload, null)
        } catch (parseErr) {
          console.error('[IssuesPage] alert_counts parse failed:', parseErr)
          vm.summaryAlertCounts = {}
        }

        vm.updateFilters()
        vm.$forceUpdate()
      } catch (handlerErr) {
        console.error('[IssuesPage] alert_counts handler failed:', handlerErr)
      }
      oneDone()
    })
    .catch(function (err) {
      if (isRequestAborted(err) || !gate.isCurrent(gen)) {
        oneDone()
        return
      }
      console.error('[IssuesPage] alert_counts failed:', err)
      if (vm.loading) {
        var errorMsg = 'Failed to load alerts'
        if (err && err.body && err.body.message) {
          errorMsg = err.body.message
        } else if (err && err.message) {
          errorMsg = err.message
        }
        vm.error = errorMsg
        vm.loading = false
      }
      oneDone()
    })
}



// WEBPACK FOOTER //
// ./src/utils/issuesSummaryFetch.js